import React, { useRef, useEffect, useCallback } from 'react';
import { useAccessibility } from './hooks/useAccessibility';
import { TRANSLATIONS, FONT_SIZES, LINE_HEIGHTS, LETTER_SPACINGS, WORD_SPACINGS } from './config/settings';
import { trapFocus, focusFirstElement } from './utils/focusManager';

function ToggleBtn({ label, active, onClick, icon }) {
  return (
    <button
      className={'pb-a11y-btn' + (active ? ' pb-a11y-btn--on' : '')}
      onClick={onClick}
      aria-pressed={active}
      type="button"
    >
      {icon && <span className="pb-a11y-btn-icon" aria-hidden="true">{icon}</span>}
      <span className="pb-a11y-btn-label">{label}</span>
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="pb-a11y-section" role="group" aria-label={title}>
      <h3 className="pb-a11y-section-title">{title}</h3>
      <div className="pb-a11y-section-body">{children}</div>
    </div>
  );
}

function SizeRow({ label, value, onInc, onDec, onReset, min, max, current, t }) {
  return (
    <div className="pb-a11y-size-row">
      <div className="pb-a11y-size-label">{label}</div>
      <div className="pb-a11y-size-controls">
        <button className="pb-a11y-size-btn" onClick={onDec} disabled={current <= min} aria-label={t.decrease} type="button">−</button>
        <span className="pb-a11y-size-value">{value}</span>
        <button className="pb-a11y-size-btn" onClick={onInc} disabled={current >= max} aria-label={t.increase} type="button">+</button>
        <button className="pb-a11y-size-btn pb-a11y-size-btn--reset" onClick={onReset} type="button">{t.resetFont}</button>
      </div>
    </div>
  );
}

function ToggleRow({ label, active, onToggle }) {
  return (
    <label className={'pb-a11y-toggle-row' + (active ? ' pb-a11y-toggle-row--on' : '')}>
      <span className="pb-a11y-toggle-label">{label}</span>
      <span className="pb-a11y-toggle-switch" aria-hidden="true">
        <span className="pb-a11y-toggle-knob" />
      </span>
      <input
        type="checkbox"
        className="pb-a11y-toggle-input"
        checked={active}
        onChange={onToggle}
        aria-label={label}
      />
    </label>
  );
}

export default function AccessibilityPanel({ onClose }) {
  const { settings, updateSetting, toggleSetting, resetAll, handleFontSizeChange } = useAccessibility();
  const panelRef = useRef(null);
  const t = TRANSLATIONS[settings.language] || TRANSLATIONS.en;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      trapFocus(panelRef.current, e);
    }
  }, [onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    if (panel) {
      panel.focus();
      focusFirstElement(panel);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const fontSizeVal = FONT_SIZES[settings.fontSize] || 16;

  return (
    <div
      className="pb-a11y-panel"
      role="dialog"
      aria-modal="true"
      aria-label={t.panelTitle}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="pb-a11y-panel-header">
        <h2 className="pb-a11y-panel-title">{t.accessibility}</h2>
        <button className="pb-a11y-close-btn" onClick={onClose} aria-label={t.close} type="button">
          ✕
        </button>
      </div>

      <div className="pb-a11y-panel-body">
        <Section title={t.typography}>
          <SizeRow
            label={t.fontSize}
            value={fontSizeVal + 'px'}
            current={settings.fontSize}
            min={0}
            max={FONT_SIZES.length - 1}
            onInc={() => handleFontSizeChange('inc')}
            onDec={() => handleFontSizeChange('dec')}
            onReset={() => handleFontSizeChange('reset')}
            t={t}
          />
          <SizeRow
            label={t.lineHeight}
            value={LINE_HEIGHTS[settings.lineHeight]?.label || 'Default'}
            current={settings.lineHeight}
            min={0}
            max={LINE_HEIGHTS.length - 1}
            onInc={() => updateSetting('lineHeight', Math.min(settings.lineHeight + 1, LINE_HEIGHTS.length - 1))}
            onDec={() => updateSetting('lineHeight', Math.max(settings.lineHeight - 1, 0))}
            onReset={() => updateSetting('lineHeight', 0)}
            t={t}
          />
          <SizeRow
            label={t.letterSpacing}
            value={LETTER_SPACINGS[settings.letterSpacing]?.label || 'Default'}
            current={settings.letterSpacing}
            min={0}
            max={LETTER_SPACINGS.length - 1}
            onInc={() => updateSetting('letterSpacing', Math.min(settings.letterSpacing + 1, LETTER_SPACINGS.length - 1))}
            onDec={() => updateSetting('letterSpacing', Math.max(settings.letterSpacing - 1, 0))}
            onReset={() => updateSetting('letterSpacing', 0)}
            t={t}
          />
          <SizeRow
            label={t.wordSpacing}
            value={WORD_SPACINGS[settings.wordSpacing]?.label || 'Default'}
            current={settings.wordSpacing}
            min={0}
            max={WORD_SPACINGS.length - 1}
            onInc={() => updateSetting('wordSpacing', Math.min(settings.wordSpacing + 1, WORD_SPACINGS.length - 1))}
            onDec={() => updateSetting('wordSpacing', Math.max(settings.wordSpacing - 1, 0))}
            onReset={() => updateSetting('wordSpacing', 0)}
            t={t}
          />
          <ToggleRow label={t.boldText} active={settings.boldText} onToggle={() => toggleSetting('boldText')} />
          <ToggleRow label={t.dyslexiaFont} active={settings.dyslexiaFont} onToggle={() => toggleSetting('dyslexiaFont')} />
        </Section>

        <Section title={t.visual}>
          <div className="pb-a11y-grid">
            <ToggleBtn label={t.darkContrast} active={settings.darkContrast} onClick={() => toggleSetting('darkContrast')} icon="🌙" />
            <ToggleBtn label={t.lightContrast} active={settings.lightContrast} onClick={() => toggleSetting('lightContrast')} icon="☀️" />
            <ToggleBtn label={t.highContrast} active={settings.highContrast} onClick={() => toggleSetting('highContrast')} icon="◐" />
            <ToggleBtn label={t.invertColors} active={settings.invertColors} onClick={() => toggleSetting('invertColors')} icon="🔄" />
            <ToggleBtn label={t.monochrome} active={settings.monochrome} onClick={() => toggleSetting('monochrome')} icon="⚫" />
            <ToggleBtn label={t.highSaturation} active={settings.highSaturation} onClick={() => toggleSetting('highSaturation')} icon="🎨" />
            <ToggleBtn label={t.lowSaturation} active={settings.lowSaturation} onClick={() => toggleSetting('lowSaturation')} icon="🌈" />
          </div>
        </Section>

        <Section title={t.navigation}>
          <div className="pb-a11y-grid">
            <ToggleBtn label={t.highlightLinks} active={settings.highlightLinks} onClick={() => toggleSetting('highlightLinks')} icon="🔗" />
            <ToggleBtn label={t.highlightHeadings} active={settings.highlightHeadings} onClick={() => toggleSetting('highlightHeadings')} icon="📑" />
            <ToggleBtn label={t.readingGuide} active={settings.readingGuide} onClick={() => toggleSetting('readingGuide')} icon="📏" />
            <ToggleBtn label={t.readingMask} active={settings.readingMask} onClick={() => toggleSetting('readingMask')} icon="👁️" />
            <ToggleBtn label={t.focusMode} active={settings.focusMode} onClick={() => toggleSetting('focusMode')} icon="🔲" />
            <ToggleBtn label={t.keyboardNav} active={settings.keyboardNav} onClick={() => toggleSetting('keyboardNav')} icon="⌨️" />
            <ToggleBtn label={t.underlineLinks} active={settings.underlineLinks} onClick={() => toggleSetting('underlineLinks')} icon="🖊️" />
            <ToggleBtn label={t.hideImages} active={settings.hideImages} onClick={() => toggleSetting('hideImages')} icon="🚫" />
          </div>
        </Section>

        <Section title={t.colorBlind}>
          <div className="pb-a11y-grid">
            <ToggleBtn label={t.protanopia} active={settings.protanopia} onClick={() => { toggleSetting('protanopia'); if (settings.deuteranopia) updateSetting('deuteranopia', false); if (settings.tritanopia) updateSetting('tritanopia', false); }} icon="🔴" />
            <ToggleBtn label={t.deuteranopia} active={settings.deuteranopia} onClick={() => { toggleSetting('deuteranopia'); if (settings.protanopia) updateSetting('protanopia', false); if (settings.tritanopia) updateSetting('tritanopia', false); }} icon="🟢" />
            <ToggleBtn label={t.tritanopia} active={settings.tritanopia} onClick={() => { toggleSetting('tritanopia'); if (settings.protanopia) updateSetting('protanopia', false); if (settings.deuteranopia) updateSetting('deuteranopia', false); }} icon="🔵" />
          </div>
        </Section>

        <Section title={t.cursor}>
          <div className="pb-a11y-grid">
            <ToggleBtn label={t.largeCursor} active={settings.largeCursor} onClick={() => { toggleSetting('largeCursor'); if (settings.extraLargeCursor) updateSetting('extraLargeCursor', false); }} icon="🖱️" />
            <ToggleBtn label={t.extraLargeCursor} active={settings.extraLargeCursor} onClick={() => { toggleSetting('extraLargeCursor'); if (settings.largeCursor) updateSetting('largeCursor', false); }} icon="🖱️" />
          </div>
        </Section>

        <Section title={t.motion}>
          <div className="pb-a11y-grid">
            <ToggleBtn label={t.stopAnimations} active={settings.stopAnimations} onClick={() => toggleSetting('stopAnimations')} icon="⏸️" />
            <ToggleBtn label={t.reduceTransitions} active={settings.reduceTransitions} onClick={() => toggleSetting('reduceTransitions')} icon="🐢" />
            <ToggleBtn label={t.disableAutoplay} active={settings.disableAutoplay} onClick={() => toggleSetting('disableAutoplay')} icon="⏹️" />
          </div>
        </Section>

        <Section title={t.language}>
          <div className="pb-a11y-grid">
            <ToggleBtn label={t.english} active={settings.language === 'en'} onClick={() => updateSetting('language', 'en')} />
            <ToggleBtn label={t.nepali} active={settings.language === 'ne'} onClick={() => updateSetting('language', 'ne')} />
          </div>
        </Section>

        <div className="pb-a11y-reset-wrap">
          <button className="pb-a11y-reset-btn" onClick={resetAll} type="button">
            {t.reset}
          </button>
        </div>
      </div>
    </div>
  );
}
